import { useState } from 'preact/hooks'
import {
  formatOdds,
  kickoffTime,
  marketLocale,
  resolveApiBase,
  SportsApiClient,
  type OddsFormat,
  type WidgetProps,
} from '@sports/core'
import { useFeed } from '../../use-feed'
import { useLabels } from '../../use-labels'
import { useTimeZone } from '../../use-time-zone'

export const RACE_CENTER_ATTRIBUTES = ['sport', 'lang', 'heading', 'limit', 'refresh', 'api-base', 'theme'] as const

interface Props extends WidgetProps {
  sport?: string
  lang?: string
  heading?: string
  limit?: string
  refresh?: string
  apiBase?: string
}

interface Race {
  id: string
  name: string
  track: string
  start: string | null
  qualifying: string | null
  status: 'scheduled' | 'live' | 'finished'
  results: { pos: string; name: string; team: string; time: string }[]
  odds: { name: string; price: number }[]
}
interface BookmakerResponse {
  oddsFormat?: OddsFormat
  brand: { name?: string; logo?: string | null; link?: string | null } | null
}

export const raceCenterStyles = `
  .rc-bar { display: flex; justify-content: flex-end; padding: .6rem 1rem; border-bottom: 1px solid var(--sports-border); }
  .rc-btn {
    font: inherit; font-size: .8rem; padding: .35rem .75rem; border-radius: 6px; cursor: pointer;
    border: 1px solid var(--sports-border); background: var(--sports-bg); color: var(--sports-fg);
  }
  .rc-btn:hover { border-color: var(--sports-accent); }
  .gp { margin: .75rem; border: 1px solid var(--sports-border); border-radius: 8px; overflow: hidden; }
  .gp-head {
    display: grid; grid-template-columns: auto 1fr auto auto; gap: .75rem; align-items: center;
    padding: .7rem .9rem; cursor: pointer; background: color-mix(in srgb, var(--sports-muted) 7%, transparent);
    border: 0; width: 100%; text-align: left; font: inherit; color: inherit;
  }
  .gp-flag {
    width: 2rem; height: 2rem; border-radius: 50%; display: grid; place-items: center;
    background: var(--sports-accent); color: #fff; font-size: .9rem;
  }
  .gp-name { font-weight: 600; font-size: .95rem; }
  .gp-meta { color: var(--sports-muted); font-size: .78rem; margin-top: .1rem; }
  .gp-status {
    font-size: .68rem; font-weight: 600; padding: .2rem .5rem; border-radius: 4px; white-space: nowrap;
    background: color-mix(in srgb, var(--sports-accent) 12%, transparent); color: var(--sports-accent);
  }
  .gp-status.live { background: #e53935; color: #fff; }
  .gp-status.finished { background: color-mix(in srgb, var(--sports-muted) 15%, transparent); color: var(--sports-muted); }
  .gp-chev { color: var(--sports-muted); transition: transform .15s; }
  .gp.open .gp-chev { transform: rotate(180deg); }
  .gp-quali { padding: .6rem .9rem; font-size: .82rem; color: var(--sports-muted); border-top: 1px solid var(--sports-border); }
  .gp-sec {
    padding: .55rem .9rem .35rem; font-size: .68rem; font-weight: 700; letter-spacing: .04em;
    text-transform: uppercase; color: var(--sports-muted); border-top: 1px solid var(--sports-border);
  }
  .gp-row {
    display: grid; grid-template-columns: 1fr auto 6rem; gap: .75rem; align-items: center;
    padding: .45rem .9rem; border-top: 1px solid var(--sports-border); font-size: .85rem;
  }
  .gp-row .px {
    min-width: 3rem; text-align: center; padding: .2rem .45rem; border-radius: 5px; font-weight: 600; font-size: .8rem;
    border: 1px solid var(--sports-border); color: var(--sports-fg); text-decoration: none;
  }
  .gp-row .px.best { background: #fde68a; border-color: #f5d75a; color: #1f2937; }
  .gp-row .bk { display: flex; justify-content: flex-end; }
  .gp-row .bk img { height: 18px; max-width: 96px; object-fit: contain; }
  .gp-res { display: grid; grid-template-columns: 2rem 1fr auto auto; gap: .75rem; padding: .4rem .9rem; border-top: 1px solid var(--sports-border); font-size: .83rem; }
  .gp-res .pos { font-weight: 700; color: var(--sports-muted); }
  .gp-res .team, .gp-res .tm { color: var(--sports-muted); font-size: .78rem; }
  .gp-note { padding: .6rem .9rem; font-size: .82rem; color: var(--sports-muted); border-top: 1px solid var(--sports-border); }
  .see-more {
    display: block; width: 100%; padding: .55rem; border: 0; border-top: 1px solid var(--sports-border);
    background: transparent; color: var(--sports-muted); font: inherit; font-size: .8rem; cursor: pointer;
  }
  .see-more:hover { color: var(--sports-fg); }
  @media (max-width: 560px) {
    .gp-head { grid-template-columns: auto 1fr auto; }
    .gp-chev { display: none; }
    .gp-row { grid-template-columns: 1fr auto 4.5rem; }
    .gp-res { grid-template-columns: 1.6rem 1fr auto; }
    .gp-res .team { display: none; }
  }
`

function number(value: string | undefined, fallback: number): number {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : fallback
}

const ODDS_SHOWN = 8
const RESULTS_SHOWN = 10

/**
 * F1 / MotoGP race centre: the season's Grands Prix (upcoming first, finished
 * on demand), each with track, start, qualifying time and the race-winner
 * prices — or the classification once run. The brand, link and odds format are
 * the locale's bookmaker, like the outright tables.
 */
export function RaceCenter({ sport = 'f1', lang = 'es', heading, limit, refresh, apiBase }: Props) {
  const base = resolveApiBase(apiBase)
  const t = useLabels(base, lang)
  const tz = useTimeZone()
  const pageSize = number(limit, 6)
  const [showFinished, setShowFinished] = useState(false)
  const [shown, setShown] = useState(pageSize)
  const [open, setOpen] = useState<Record<string, boolean>>({})
  const [allOdds, setAllOdds] = useState<Record<string, boolean>>({})

  const { data, error, loading } = useFeed(
    (signal) => new SportsApiClient(base).raceCenter<{ races: Race[] }>(sport, lang, signal),
    [base, sport, lang],
    number(refresh, 300),
  )
  const { data: display } = useFeed(
    (signal) => new SportsApiClient(base).bookmaker<BookmakerResponse>(lang, 'outright', signal),
    [base, lang],
    0,
  )
  const format: OddsFormat = display?.oddsFormat ?? 'decimal'
  const brand = display?.brand ?? null
  const link = brand?.link ?? undefined
  const locale = marketLocale(lang)

  const all = data?.races ?? []
  const upcoming = all.filter((r) => r.status !== 'finished')
  // Finished rounds read newest first, after what's still to come.
  const finished = all.filter((r) => r.status === 'finished').reverse()
  const races = showFinished ? [...upcoming, ...finished] : upcoming
  const visible = races.slice(0, shown)
  const nextId = upcoming[0]?.id

  const fullDate = (iso: string | null) => {
    if (!iso) return ''
    const d = new Date(iso)
    try {
      const day = d.toLocaleDateString(locale, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', timeZone: tz })
      return `${day} · ${kickoffTime(d, lang, tz)}`
    } catch {
      return iso.slice(0, 10)
    }
  }
  const shortDate = (iso: string | null) => {
    if (!iso) return ''
    const d = new Date(iso)
    try {
      return `${d.toLocaleDateString(locale, { day: 'numeric', month: 'short', timeZone: tz })}, ${kickoffTime(d, lang, tz)}`
    } catch {
      return iso.slice(0, 16)
    }
  }
  const statusLabel = (s: Race['status']) =>
    s === 'live' ? t('liveNow', 'En vivo') : s === 'finished' ? t('finished', 'Finalizado') : t('scheduled', 'Programada')

  const bookmaker = () => {
    if (!brand?.logo && !brand?.name) return <span class="bk" />
    const inner = brand?.logo ? <img src={brand.logo} alt={brand.name ?? ''} /> : brand?.name
    return link ? (
      <a class="bk" href={link} target="_blank" rel="sponsored noopener">
        {inner}
      </a>
    ) : (
      <span class="bk">{inner}</span>
    )
  }

  const body = (race: Race) => {
    if (race.status === 'finished') {
      if (!race.results.length) return null
      return (
        <>
          <div class="gp-sec">{t('results', 'Resultados')}</div>
          {race.results.slice(0, RESULTS_SHOWN).map((r) => (
            <div class="gp-res" key={r.pos + r.name}>
              <span class="pos">{r.pos}</span>
              <span>{r.name}</span>
              <span class="team">{r.team}</span>
              <span class="tm">{r.time}</span>
            </div>
          ))}
        </>
      )
    }
    const best = race.odds[0]?.price
    const rows = allOdds[race.id] ? race.odds : race.odds.slice(0, ODDS_SHOWN)
    return (
      <>
        {race.qualifying && (
          <div class="gp-quali">
            {t('qualifying', 'Clasificación')}: {shortDate(race.qualifying)}
          </div>
        )}
        <div class="gp-sec">{t('raceWinner', 'Ganador de la carrera')}</div>
        {race.odds.length === 0 && <div class="gp-note">{t('oddsSoon', 'Las cuotas se publicarán más cerca de la carrera.')}</div>}
        {rows.map((o) => {
          const cls = `px${o.price === best ? ' best' : ''}`
          const price = formatOdds(o.price, format)
          return (
            <div class="gp-row" key={o.name}>
              <span>{o.name}</span>
              {link ? (
                <a class={cls} href={link} target="_blank" rel="sponsored noopener">
                  {price}
                </a>
              ) : (
                <span class={cls}>{price}</span>
              )}
              {bookmaker()}
            </div>
          )
        })}
        {race.odds.length > ODDS_SHOWN && (
          <button class="see-more" type="button" onClick={() => setAllOdds({ ...allOdds, [race.id]: !allOdds[race.id] })}>
            {allOdds[race.id] ? t('seeLess', 'Ver menos') : `${t('seeMore', 'Ver más')} (${race.odds.length})`}
          </button>
        )}
      </>
    )
  }

  return (
    <div class="sports-card">
      {heading && (
        <div class="sports-head">
          <span>{heading}</span>
        </div>
      )}
      <div class="rc-bar">
        <button class="rc-btn" type="button" onClick={() => setShowFinished(!showFinished)}>
          {showFinished ? t('hideFinished', 'Ocultar finalizados') : t('showFinished', 'Mostrar finalizados')}
        </button>
      </div>
      {loading && !data && <div class="sports-note">{t('loading', 'Cargando…')}</div>}
      {error && !data && <div class="sports-note">{t('oddsUnavailable', 'Cuotas no disponibles.')}</div>}
      {data && races.length === 0 && <div class="sports-note">{t('noRaces', 'Sin Grandes Premios.')}</div>}
      {visible.map((race) => {
        const isOpen = open[race.id] ?? race.id === nextId
        return (
          <div class={`gp${isOpen ? ' open' : ''}`} key={race.id}>
            <button class="gp-head" type="button" onClick={() => setOpen({ ...open, [race.id]: !isOpen })}>
              <span class="gp-flag" aria-hidden="true">
                ⚑
              </span>
              <span>
                <div class="gp-name">{race.name}</div>
                <div class="gp-meta">
                  {[race.track, fullDate(race.start)].filter(Boolean).join(' · ')}
                </div>
              </span>
              <span class={`gp-status ${race.status}`}>{statusLabel(race.status)}</span>
              <span class="gp-chev" aria-hidden="true">
                ▾
              </span>
            </button>
            {isOpen && body(race)}
          </div>
        )
      })}
      {races.length > shown && (
        <button class="see-more" type="button" onClick={() => setShown(shown + pageSize)}>
          {t('seeMore', 'Ver más')} ({races.length - shown})
        </button>
      )}
    </div>
  )
}
